import React, { useState } from "react";
import { motion } from "framer-motion";
import { ToastContainer, toast, Bounce } from "react-toastify";

const CheckoutPopup = ({ product, onClose, onPaymentComplete }) => {
  const [qty, setQty] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("");

  // Payment forms states
  const [upiId, setUpiId] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");

  // Net banking extra fields
  const [bankName, setBankName] = useState("");
  const [accHolder, setAccHolder] = useState("");
  const [accNumber, setAccNumber] = useState("");
  const [ifsc, setIfsc] = useState("");

  const [address, setAddress] = useState("");

  const productTotal = product.price * qty;
  const gst = +(productTotal * 0.18).toFixed(2);
  const delivery = 199;
  const grandTotal = +(productTotal + gst + delivery).toFixed(2);

  const handleConfirm = () => {
    if (!paymentMethod) {
      toast.success('Select Payment Method', {
  position: "top-center",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: false,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "light",
  transition: Bounce,
});

      return;
    }
    if (!address.trim()) {
      toast.success('Enter Delevery Address', {
  position: "top-center",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: false,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "light",
  transition: Bounce,
});

      return;
    }

    // Payment-specific validation
    if (paymentMethod === "UPI" && !upiId.trim()) {
      alert("Enter UPI ID");
      return;
    }

    if (
      (paymentMethod === "Credit Card" || paymentMethod === "Debit Card") &&
      (!cardNumber.trim() || !cardName.trim() || !cardExpiry.trim() || !cardCvv.trim())
    ) {
      alert("Please fill card details");
      return;
    }

    if (paymentMethod === "Net Banking") {
      if (!bankName) {
        alert("Select bank");
        return;
      }
      if (!accHolder.trim() || !accNumber.trim() || !ifsc.trim()) {
        alert("Enter account holder, account number and IFSC");
        return;
      }
      // basic account number digits check
      if (!/^\d{6,20}$/.test(accNumber.replace(/\s+/g, ""))) {
        alert("Enter a valid account number (6-20 digits)");
        return;
      }
      if (!/^[A-Za-z]{4}[A-Za-z0-9]{7}$/.test(ifsc.trim().toUpperCase())) {
        // IFSC rough pattern: 4 letters + 7 alnum
        alert("Enter a valid IFSC code");
        return;
      }
    }

    // All good -> call parent
    onPaymentComplete(product, qty, paymentMethod, address);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[999]"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.22 }}
        className="bg-white w-[85%] max-w-5xl max-h-[85vh] rounded-2xl shadow-2xl overflow-y-auto p-6"
      >
        {/* HEADER */}
        <div className="flex justify-between items-center border-b pb-3 mb-4">
          <h2 className="text-2xl font-bold text-blue-700">Checkout</h2>
          <button
            onClick={onClose}
            className="text-gray-600 text-2xl hover:text-red-600 transition"
            aria-label="Close checkout"
          >
            ✖
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: SUMMARY (compact) */}
          <div className="lg:col-span-1 bg-gray-50 p-4 rounded-xl shadow-sm">
            <img
              src={product.thumbnail}
              alt={product.title}
              className="w-full h-40 object-cover rounded-md mb-3"
            />
            <h3 className="text-lg font-semibold text-gray-800 line-clamp-2">{product.title}</h3>
            <p className="text-sm text-gray-500 mb-3 line-clamp-2">{product.description}</p>

            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={() => qty > 1 && setQty(qty - 1)}
                className="px-2 py-1 bg-gray-200 rounded-md text-lg"
                aria-label="Decrease quantity"
              >
                -
              </button>
              <span className="font-medium">{qty}</span>
              <button
                onClick={() => setQty(qty + 1)}
                className="px-2 py-1 bg-blue-600 text-white rounded-md text-lg"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>

            <div className="mt-4 text-sm space-y-2">
              <div className="flex justify-between"><span>Price</span><span>₹{productTotal.toLocaleString()}</span></div>
              <div className="flex justify-between"><span>GST (18%)</span><span>₹{gst.toLocaleString()}</span></div>
              <div className="flex justify-between"><span>Delivery</span><span>₹{delivery}</span></div>
              <hr className="my-2" />
              <div className="flex justify-between font-semibold text-lg"><span>Total</span><span>₹{grandTotal.toLocaleString()}</span></div>
            </div>
          </div>

          {/* RIGHT: FORMS (compact spacing) */}
          <div className="lg:col-span-2 bg-gray-50 p-4 rounded-xl shadow-sm">
            {/* Address */}
            <label className="block mb-2 font-medium">Delivery Address</label>
            <textarea
              rows={3}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full p-2 border rounded-md mb-4 text-sm"
              placeholder="House No, Street, City, Pincode"
            />

            {/* Payment methods */}
            <label className="block mb-2 font-medium">Payment Method</label>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {["UPI", "Credit Card", "Debit Card", "Net Banking", "Cash on Delivery"].map(
                (method) => (
                  <button
                    key={method}
                    onClick={() => setPaymentMethod(method)}
                    className={`py-2 px-3 text-sm rounded-md border text-left ${
                      paymentMethod === method
                        ? "bg-blue-600 text-white border-blue-700"
                        : "bg-white text-gray-700"
                    }`}
                  >
                    {method}
                  </button>
                )
              )}
            </div>

            {/* Dynamic small forms */}
            {/* UPI */}
            {paymentMethod === "UPI" && (
              <div className="mb-4 p-3 bg-white border rounded-md">
                <label className="block text-sm font-medium mb-1">UPI ID</label>
                <input
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  placeholder="example@upi"
                  className="w-full p-2 border rounded-md text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">You will be prompted to approve payment from this UPI ID.</p>
              </div>
            )}

            {/* Card */}
            {(paymentMethod === "Credit Card" || paymentMethod === "Debit Card") && (
              <div className="mb-4 p-3 bg-white border rounded-md">
                <div className="mb-2">
                  <label className="block text-sm font-medium">Card Number</label>
                  <input
                    maxLength="19"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(e.target.value.replace(/[^\d\s]/g, "").replace(/(.{4})/g, "$1 ").trim())}
                    placeholder="XXXX XXXX XXXX XXXX"
                    className="w-full p-2 border rounded-md text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium">Name on Card</label>
                    <input
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                      placeholder="Full name"
                      className="w-full p-2 border rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">Expiry (MM/YY)</label>
                    <input
                      maxLength="5"
                      value={cardExpiry}
                      onChange={(e) => setCardExpiry(e.target.value)}
                      placeholder="MM/YY"
                      className="w-full p-2 border rounded-md text-sm"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-sm font-medium">CVV</label>
                  <input
                    type="password"
                    maxLength="4"
                    value={cardCvv}
                    onChange={(e) => setCardCvv(e.target.value)}
                    placeholder="XXX"
                    className="w-32 p-2 border rounded-md text-sm"
                  />
                </div>
              </div>
            )}

            {/* Net Banking with extra fields */}
            {paymentMethod === "Net Banking" && (
              <div className="mb-4 p-3 bg-white border rounded-md space-y-3">
                <div>
                  <label className="block text-sm font-medium">Select Bank</label>
                  <select
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    className="w-full p-2 border rounded-md text-sm"
                  >
                    <option value="">Choose bank</option>
                    <option value="SBI">State Bank of India</option>
                    <option value="HDFC">HDFC Bank</option>
                    <option value="ICICI">ICICI Bank</option>
                    <option value="AXIS">Axis Bank</option>
                    <option value="KOTAK">Kotak Mahindra</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium">Account Holder Name</label>
                  <input
                    value={accHolder}
                    onChange={(e) => setAccHolder(e.target.value)}
                    placeholder="Full name as per bank"
                    className="w-full p-2 border rounded-md text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium">Account Number</label>
                    <input
                      value={accNumber}
                      onChange={(e) => setAccNumber(e.target.value.replace(/\s+/g, ""))}
                      placeholder="Enter account number"
                      className="w-full p-2 border rounded-md text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">IFSC Code</label>
                    <input
                      value={ifsc}
                      onChange={(e) => setIfsc(e.target.value.toUpperCase())}
                      placeholder="AAAA0BBBBBB"
                      className="w-full p-2 border rounded-md text-sm"
                    />
                  </div>
                </div>

                <p className="text-xs text-gray-500">We'll use these details to process net-banking transfer. Do not share sensitive info publicly.</p>
              </div>
            )}

            {/* Cash on Delivery */}
            {paymentMethod === "Cash on Delivery" && (
              <div className="mb-4 p-3 bg-white border rounded-md">
                <p className="text-sm">You will pay ₹{grandTotal.toLocaleString()} at the time of delivery.</p>
              </div>
            )}

            {/* Confirm */}
            <div className="pt-2">
              <button
                onClick={handleConfirm}
                className="w-full bg-green-600 text-white py-2 rounded-md font-semibold hover:bg-green-700 transition"
              >
                Pay ₹{grandTotal.toLocaleString()}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CheckoutPopup;
